import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarRange,
  ChevronsDownUp,
  ChevronsUpDown,
  Plus,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { itineraryApi, tripPlanApi } from "@/lib/api";
import type {
  ItemSegment,
  ItineraryItemType,
  ItineraryResponse,
  PlanLeg,
  TripPlanResponse,
} from "@/lib/types";
import ItineraryItemDialog from "../itinerary/ItineraryItemDialog";
import ItineraryDayCard from "./ItineraryDayCard";
import CruiseImportCard from "./CruiseImportCard";
import {
  dayHeading,
  flightsOf,
  initialExpandedDates,
} from "./itineraryDay-utils";

export interface DayItineraryProps {
  tripId: string;
  /** Email deep-link (?addStop=1&date=…&type=…) — opens the add dialog prefilled. */
  addStopRequest?: { date?: string; type?: ItineraryItemType } | null;
  onAddStopConsumed?: () => void;
  /** The printed document: every day open, no controls, no fares. */
  printMode?: boolean;
}

/**
 * The trip as a day-by-day document.
 *
 * One collapsible card per calendar day, continuous from the first day to the
 * last — the backend spine guarantees the empty days are present, which is what
 * finally makes "add something to next Tuesday" possible. Fares live behind a
 * closed door on the days that have flights; they are not deleted, because
 * during a fare search that detail is the point.
 *
 * `printMode` renders the same component as a paper document. There is
 * deliberately no second renderer: a print view that drifts from the screen
 * view is a print view that lies.
 */
export default function DayItinerary({
  tripId,
  addStopRequest,
  onAddStopConsumed,
  printMode = false,
}: DayItineraryProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<ItineraryResponse>({
    queryKey: ["itinerary", tripId],
    queryFn: () => itineraryApi.get(tripId),
    enabled: !!tripId,
  });

  // Shares UnifiedItinerary's cache key, so switching views costs no request.
  // Skipped entirely in print — the printed page shows no fares.
  const { data: plan } = useQuery<TripPlanResponse>({
    queryKey: ["tripPlan", tripId],
    queryFn: () => tripPlanApi.get(tripId),
    enabled: !!tripId && !printMode,
  });

  const [expanded, setExpanded] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ItemSegment | null>(null);
  const [defaultDate, setDefaultDate] = useState<string | undefined>();
  const [defaultType, setDefaultType] = useState<ItineraryItemType | undefined>();

  const days = data?.days ?? [];

  // Seed the open/shut state once, from the day's own content. Re-seeding on
  // every refetch would slam shut a day the traveller had just opened.
  useEffect(() => {
    if (seeded || days.length === 0) return;
    setExpanded(initialExpandedDates(days, printMode));
    setSeeded(true);
  }, [days, seeded, printMode]);

  const legByRoute = useMemo(() => {
    const map = new Map<string, PlanLeg>();
    for (const leg of plan?.legs ?? []) map.set(leg.routeId, leg);
    return map;
  }, [plan]);

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => itineraryApi.deleteItem(tripId, itemId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["itinerary", tripId] }),
  });

  const openCreate = (dayDate?: string, itemType?: ItineraryItemType) => {
    setEditing(null);
    setDefaultDate(dayDate);
    setDefaultType(itemType);
    setDialogOpen(true);
  };

  const openEdit = (seg: ItemSegment) => {
    setEditing(seg);
    setDefaultDate(seg.day_date);
    setDefaultType(undefined);
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!addStopRequest || printMode) return;
    openCreate(addStopRequest.date, addStopRequest.type);
    onAddStopConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addStopRequest]);

  const handleDelete = (seg: ItemSegment) => {
    if (window.confirm("Remove “" + seg.title + "” from the itinerary?")) {
      deleteMutation.mutate(seg.id);
    }
  };

  const toggleDay = (date: string) =>
    setExpanded((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    );

  const allOpen = days.length > 0 && expanded.length === days.length;
  const toggleAll = () =>
    setExpanded(allOpen ? [] : days.map((d) => d.date));

  const dayDates = days.map((d) => d.date);
  const range =
    days.length > 0
      ? dayHeading(days[0].date).date +
        " – " +
        dayHeading(days[days.length - 1].date).date +
        " · " +
        days.length +
        (days.length === 1 ? " day" : " days")
      : null;

  /* ── Loading / error / empty ─────────────────────────────────────────── */

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="mt-2 h-3 w-56" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card py-10 text-center">
        <AlertTriangle className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          We couldn&apos;t load your itinerary.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card py-12 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <CalendarRange className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        </span>
        <div>
          <p className="text-sm font-medium">No itinerary yet</p>
          <p className="text-xs text-muted-foreground">
            Pick your flights, or add the first thing you already booked.
          </p>
        </div>
        {!printMode && (
          <Button size="sm" onClick={() => openCreate()}>
            <Plus className="mr-1.5 h-4 w-4" /> Add your first stop
          </Button>
        )}
      </div>
    );
  }

  /* ── The document ────────────────────────────────────────────────────── */

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{range}</p>
        {!printMode && (
          <div className="flex shrink-0 items-center gap-2 print:hidden">
            <Button variant="ghost" size="sm" onClick={toggleAll}>
              {allOpen ? (
                <ChevronsDownUp className="mr-1.5 h-4 w-4" />
              ) : (
                <ChevronsUpDown className="mr-1.5 h-4 w-4" />
              )}
              {allOpen ? "Collapse all" : "Expand all"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/trips/" + tripId + "/print")}
            >
              <Printer className="mr-1.5 h-4 w-4" /> Print / Save PDF
            </Button>
          </div>
        )}
      </div>

      {/* A sailing whose days were never read is a seven-night hole in the
          itinerary. Offer to fill it from the cruise line's own page — and only
          while there is something to fill. */}
      {!printMode && data?.cruiseImportAvailable && (
        <CruiseImportCard tripId={tripId} linkKnown={!!data.cruiseLinkKnown} />
      )}

      <div className="space-y-2">
        {days.map((day, i) => (
          <ItineraryDayCard
            key={day.date}
            tripId={tripId}
            day={day}
            index={i}
            expanded={expanded.includes(day.date)}
            onToggle={toggleDay}
            onAdd={(date) => openCreate(date)}
            onEditItem={openEdit}
            onDeleteItem={handleDelete}
            deletingId={
              deleteMutation.isPending
                ? (deleteMutation.variables as string)
                : null
            }
            legs={flightsOf(day)
              .map((f) => legByRoute.get(f.routeId))
              .filter((l): l is PlanLeg => !!l)}
            printMode={printMode}
          />
        ))}
      </div>

      {!printMode && (
        <ItineraryItemDialog
          tripId={tripId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editing={editing}
          dayDates={dayDates}
          defaultDate={defaultDate}
          defaultType={defaultType}
          companions={data?.companions ?? []}
        />
      )}
    </section>
  );
}
